import React, { useEffect, useState } from 'react';
import { jsx as _jsx } from 'react/jsx-runtime';
import styled from 'styled-components';
import Modal, { Sheets, ModalBody, ModalHeader } from '@ingka/modal';
import Checkbox from '@ingka/checkbox';
import useTranslation from '../../hooks/useTranslation';
import TMarkUp from '../TMarkUp';
import AdditionalItemListing from '../AdditionalItemListing';
import TermsAndConditionsLabel from './TermsAndConditionsLabel';
import cssIngka from '@ingka/variables/colours-css';

const nl2br = (str: string): JSX.Element[] => {
  if (typeof str !== 'string') {
    return str;
  }
  const newlineRegex = /(\r\n|\r|\n)/g;
  return str.split(newlineRegex).map((line, index) => {
    if (line.match(newlineRegex)) {
      return _jsx('br', { key: index });
    }
    return line;
  });
};
/* global CSS_CONFIG */
const formSettings = CSS_CONFIG.FORMSETTINGS;

export interface TACRendererProps {
  value: string;
  validate: (checked: boolean) => string | null;
  onChange: (checked: boolean, name: string) => void;
}

const SparePartsPolicyTitle = styled.h2.attrs({ as: 'h2', className: 'h2' })`
  margin-bottom: 24px;
`;

const PrivacyPolicyExtraLink = styled.a`
  text-decoration: underline;
  font-weight: bold;
  display: inline;
`;
const AdditionalPrivacyParagraph = styled.p`
  margin-inline-start: 2.75rem;
  margin-top: 1rem;
`;

const InlineErrorMessage = styled.span`
  color: ${cssIngka.colourSemanticNegative};
  font-size: 0.75rem;
  margin-top: 0.75rem;
  display: inline-block;
`;

interface TermsAndConditionsRendererProps {
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showCheckbox: boolean;
  isSubmitBtnClicked: boolean;
}
const TermsAndConditionsRenderer: React.FC<TermsAndConditionsRendererProps> = ({
  onChange,
  checked,
  showCheckbox,
  isSubmitBtnClicked,
}): JSX.Element => {
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();
  const refPrivacyLabel = React.useRef<HTMLAnchorElement>(null);
  const additionalListingLabel = t('orderForm.extraAdditionalItemsLabel');
  const extraReturnLink = t('orderForm.extraReturnLink');

  const onChangeModal =
    (toggle: boolean) =>
    (e: React.SyntheticEvent): void => {
      e.preventDefault();
      setModalOpen(toggle);
    };
  const openModalOnEnter = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter') setModalOpen(true);
  };
  const onModalClosed = (): void => {
    if (refPrivacyLabel && refPrivacyLabel.current)
      refPrivacyLabel.current.focus();
  };
  const [valid, setValid] = useState(false);

  useEffect((): void => {
    setValid(!checked && isSubmitBtnClicked);
  }, [isSubmitBtnClicked, checked]);

  return (
    <>
      {formSettings.additionalInformationLabel && (
        <AdditionalItemListing
          label={additionalListingLabel}
          linkText={extraReturnLink}
        />
      )}
      {showCheckbox ? (
        <>
          <Checkbox
            id="tos-checkbox"
            name="terms"
            type="checkbox"
            value=""
            checked={checked}
            onChange={onChange}
            label={TermsAndConditionsLabel(
              openModalOnEnter,
              refPrivacyLabel,
              onChangeModal
            )}
          />
          {valid ? (
            <InlineErrorMessage>
              {t('validationBlock.agreeTerms')}
            </InlineErrorMessage>
          ) : null}
        </>
      ) : (
        TermsAndConditionsLabel(
          openModalOnEnter,
          refPrivacyLabel,
          onChangeModal
        )
      )}
      {formSettings.addExtraPrivacyPolicyText && (
        <AdditionalPrivacyParagraph>
          <TMarkUp value={t('orderForm.extraPrivacyPolicyExtraText')} />
          &nbsp;
          <PrivacyPolicyExtraLink
            href={CSS_CONFIG.EXTRAPRIVACYPOLICYLINK}
            target="_blank"
          >
            <TMarkUp value={t('orderForm.extraPrivacyPolicyTextHighlighted')} />
          </PrivacyPolicyExtraLink>
        </AdditionalPrivacyParagraph>
      )}
      <Modal
        visible={modalOpen}
        handleCloseBtn={onChangeModal(false)}
        onModalClosed={onModalClosed}
      >
        <Sheets
          alignment="right"
          size="small"
          footer={null}
          header={
            <ModalHeader
              ariaCloseTxt={t('orderForm.closePrivacyPolicyModal')}
              closeBtnClick={onChangeModal(false)}
            />
          }
          preserveAlignment
        >
          <ModalBody>
            <SparePartsPolicyTitle>
              <TMarkUp value={t('orderForm.sparePartsPolicyLabel')} />
            </SparePartsPolicyTitle>
            {formSettings.showModalTextWithLink ? (
              <p>
                {nl2br(t('orderForm.termsAndConditionsWithLinkFirstSection'))}
                <a
                  href={CSS_CONFIG.TERMSANDPOLICYMODALLINK}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {nl2br(t('orderForm.termsAndConditionsLink'))}
                </a>
                {nl2br(t('orderForm.termsAndConditionsWithLinkSecondSection'))}
              </p>
            ) : (
              <p>{nl2br(t('orderForm.termsAndConditions'))}</p>
            )}
          </ModalBody>
        </Sheets>
      </Modal>
    </>
  );
};

export default TermsAndConditionsRenderer;
